/**
 * Quechua render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'qu')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const quechuaLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'paqariy' },
    change: { primary: 'kambiay' },
    click: { primary: 'ñitiy' },
    dblclick: { primary: 'dblclick' },
    focus: { primary: 'qhaway' },
    input: { primary: 'yaykuchiy' },
    keydown: { primary: 'llave uray' },
    keypress: { primary: 'keypress' },
    keyup: { primary: 'llave hawa' },
    load: { primary: 'apakuy' },
    mousedown: { primary: 'ratñitiy' },
    mouseenter: { primary: 'mouseenter' },
    mouseleave: { primary: 'mouseleave' },
    mousemove: { primary: 'mousemove' },
    mouseout: { primary: 'mouseout' },
    mouseover: { primary: 'mouseover' },
    mouseup: { primary: 'rathuqariy' },
    reset: { primary: 'musuqchay' },
    resize: { primary: 'hatun_kay' },
    scroll: { primary: 'kunray' },
    submit: { primary: 'apaykachay' },
    touchcancel: { primary: 'touchcancel' },
    touchend: { primary: 'touchend' },
    touchmove: { primary: 'touchmove' },
    touchstart: { primary: 'touchstart' },
    unload: { primary: 'unload' },
  },
  logical: {
    and: { primary: 'hinallataq' },
    changes: { primary: 'tukurikun' },
    contains: { primary: 'ukupi_kan' },
    else: { primary: 'manachus' },
    end: { primary: 'tukuy' },
    equals: { primary: 'kikin' },
    exists: { primary: 'tiyan' },
    has: { primary: 'kachkan' },
    have: { primary: 'kachkani' },
    includes: { primary: 'churasqa' },
    is: { primary: 'kanqa' },
    live: { primary: 'kawsay' },
    matches: { primary: 'tupan' },
    not: { primary: 'mana' },
    or: { primary: 'utaq' },
    otherwise: { primary: 'huk_kaqpi' },
    then: { primary: 'chayqa' },
    when: { primary: 'maykama' },
    where: { primary: 'maypi' },
  },
  temporal: {
    forever: { primary: 'wiñay' },
    h: { primary: 'h' },
    hour: { primary: 'hora' },
    hours: { primary: 'horakuna' },
    millisecond: { primary: 'iskay_paqta_sikundu' },
    milliseconds: { primary: 'iskay_paqta_sikundukuna' },
    min: { primary: 'm' },
    minute: { primary: 'minutu' },
    minutes: { primary: 'minutukuna' },
    ms: { primary: 'ms' },
    s: { primary: 's' },
    second: { primary: 'sikundu' },
    seconds: { primary: 'sikundukuna' },
    times: { primary: 'kuti' },
  },
  values: {
    body: { primary: 'kurku' },
    detail: { primary: 'sut_iy' },
    document: { primary: 'qillqa' },
    element: { primary: 'raku' },
    event: { primary: 'ruway' },
    false: { primary: 'llulla' },
    it: { primary: 'chay' },
    its: { primary: 'chaypaq' },
    me: { primary: 'noqa' },
    my: { primary: 'noqaq' },
    myself: { primary: 'noqa killa' },
    null: { primary: 'null' },
    result: { primary: 'lluqsiy' },
    target: { primary: 'punta' },
    true: { primary: 'cheqaq' },
    undefined: { primary: 'mana_riqsisqa' },
    value: { primary: 'chanin' },
    window: { primary: 'k_iri' },
    you: { primary: 'qam' },
    your: { primary: 'qampaq' },
    yourself: { primary: 'qam killa' },
  },
  attributes: {
    attribute: { primary: 'kaq' },
    attributes: { primary: 'kaqkuna' },
    class: { primary: 'ayllu' },
    classes: { primary: 'ayllukuna' },
    properties: { primary: 'kanaykuna' },
    property: { primary: 'kanay' },
    style: { primary: 'sami' },
    styles: { primary: 'samikuna' },
  },
  expressions: {
    up: { primary: 'hanaq' },
    down: { primary: 'uray' },
    left: { primary: 'lluqi' },
    right: { primary: 'paña' },
    at: { primary: 'pi' },
    back: { primary: 'kutiy' },
    characters: { primary: 'sanampa' },
    children: { primary: 'wawakuna' },
    closest: { primary: 'kaylla' },
    elsewhere: { primary: 'hawa' },
    empty: { primary: 'chusaq' },
    'ends with': { primary: 'tukusqa wan' },
    first: { primary: 'ñawpaq' },
    'ignoring case': { primary: 'hatun-huchuy qillqata-saqiy' },
    index: { primary: 'yupay' },
    'joined by': { primary: 'huñusqa' },
    last: { primary: 'qhipa' },
    'mapped to': { primary: 'kay kaqman' },
    next: { primary: 'qhipantin' },
    no: { primary: 'mana_kanchu' },
    parent: { primary: 'mama_tayta' },
    prev: { primary: 'ñawpaq' },
    previous: { primary: 'ñawpaqnin' },
    random: { primary: 'imaymanata' },
    some: { primary: 'wakin' },
    'sorted by': { primary: 'niqpi ruwasqa' },
    'split by': { primary: 'rakisqa' },
    start: { primary: 'qallay' },
    catch: { primary: 'hapsiy' },
    finally: { primary: 'qhipamanqa' },
    def: { primary: 'rurana' },
    'starts with': { primary: 'qallarisqa wan' },
    within: { primary: 'ukupi' },
  },
};

registerLexicon('qu', quechuaLexicon);
